import { CheckIcon, Loader2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { DialogFooter } from '@/components/ui/dialog.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group.js';
import {
  WorkspaceNameTakenError,
  useCreateWorkspace,
  useFindSimilarWorkspaces,
  useRequestWorkspaceJoin,
  type SimilarWorkspace,
  type Workspace,
  type WorkspaceSimilarity,
  type WorkspaceSimilaritySignal,
} from '@/lib/api/workspaces.js';
import { notify } from '@/lib/notify.js';

function reason(signal: WorkspaceSimilaritySignal, strength: string): string {
  switch (signal) {
    case 'case_variant':
      return 'Same name, different capitals';
    case 'substring':
      return 'One name contains the other';
    case 'shared_token':
      return strength === 'weak'
        ? 'Shares a common word'
        : 'Shares a distinctive word';
    case 'token_inside':
      return 'Shares part of a word';
    default:
      return 'Similar name';
  }
}

interface NewWorkspaceDialogProps {
  name: string;
  onNameChange: (name: string) => void;
  onCreated: (workspace: Workspace) => void;
  onOpenChange: (open: boolean) => void;
  onOpenWorkspace: (id: string) => Promise<void>;
}

export function NewWorkspaceDialog({
  name,
  onNameChange,
  onCreated,
  onOpenChange,
  onOpenWorkspace,
}: NewWorkspaceDialogProps) {
  const create = useCreateWorkspace();
  const check = useFindSimilarWorkspaces();
  const join = useRequestWorkspaceJoin();
  const [lookup, setLookup] = useState<WorkspaceSimilarity | null>(null);
  const [checkedName, setCheckedName] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<
    'checking' | 'creating' | 'joining' | 'opening' | null
  >(null);
  const pending = useRef(false);
  const selected = lookup?.matches.find((match) => match.id === selectedId);

  function editName(value: string) {
    onNameChange(value);
    setLookup(null);
    setSelectedId('');
    setError(null);
  }

  async function submitNew() {
    const finalName = name.trim();
    if (!finalName || pending.current) return;
    pending.current = true;
    setBusy('checking');
    setError(null);
    const reviewed = lookup !== null && checkedName === finalName;
    try {
      const result = await check.mutateAsync(finalName);
      setLookup(result);
      setCheckedName(finalName);
      if (result.exactMatch) {
        if (result.suggestedName) onNameChange(result.suggestedName);
        setError(
          'A workspace with that name already exists. Review the suggested name or choose another.',
        );
        return;
      }
      if (result.matches.length && !reviewed) return;
      setBusy('creating');
      onCreated(await create.mutateAsync({ name: finalName }));
    } catch (failure) {
      if (failure instanceof WorkspaceNameTakenError) {
        onNameChange(failure.suggestedName);
        setLookup((previous) => ({
          exactMatch: true,
          suggestedName: failure.suggestedName,
          matches: previous?.matches ?? [],
        }));
        setCheckedName(finalName);
      }
      setError(
        failure instanceof Error
          ? failure.message
          : 'Could not create workspace. Please try again.',
      );
    } finally {
      pending.current = false;
      setBusy(null);
    }
  }

  async function requestToJoin() {
    if (!selected || pending.current) return;
    pending.current = true;
    setBusy(selected.isMember ? 'opening' : 'joining');
    setError(null);
    try {
      if (selected.isMember) {
        await onOpenWorkspace(selected.id);
        onOpenChange(false);
        return;
      }
      await join.mutateAsync(selected.id);
      notify.success(
        `The owner of “${selected.name}” was asked in Teams. You will be notified when they decide.`,
      );
      onOpenChange(false);
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : selected.isMember
            ? 'Could not open workspace. Please try again.'
            : 'Could not request access. Please try again.',
      );
    } finally {
      pending.current = false;
      setBusy(null);
    }
  }

  function matchGroup(matches: SimilarWorkspace[], label: string) {
    if (!matches.length) return null;
    return (
      <div className="w-full min-w-0">
        <h3 className="px-3 pb-1 pt-2 text-xs font-medium text-muted-foreground">
          {label}
        </h3>
        <div className="divide-y divide-border">
          {matches.map((match) => (
            <ToggleGroupItem
              key={match.id}
              value={match.id}
              className="h-auto min-h-11 w-full justify-start gap-3 rounded-md px-3 py-3 text-left"
            >
              <CheckIcon
                aria-hidden
                className={
                  selectedId === match.id
                    ? 'size-4 shrink-0 text-primary-text'
                    : 'size-4 shrink-0 invisible'
                }
              />
              <span className="min-w-0 flex-1 space-y-1">
                <span className="block break-words text-sm font-medium text-foreground">
                  {match.name}
                </span>
                <span className="block break-words text-xs font-normal text-muted-foreground">
                  {match.ownerNames.length
                    ? `Owners: ${match.ownerNames.join(', ')}`
                    : 'Owner name unavailable'}
                </span>
                <span className="block break-words text-xs font-normal text-muted-foreground">
                  {match.signals
                    .map((signal) => reason(signal, match.strength))
                    .join('; ')}
                </span>
                {match.isMember && (
                  <span className="block text-xs font-normal text-muted-foreground">
                    Already a member
                  </span>
                )}
              </span>
            </ToggleGroupItem>
          ))}
        </div>
      </div>
    );
  }

  return (
    <AppDialog
      open
      title="New workspace"
      description="Check whether your project already has a workspace."
      onOpenChange={(open) => {
        if (!pending.current) onOpenChange(open);
      }}
      showCloseButton={!busy}
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        (event.target as HTMLElement).focus();
      }}
    >
      <div className="space-y-2" aria-busy={!!busy}>
        <Label htmlFor="workspace-name">Name</Label>
        <Input
          id="workspace-name"
          value={name}
          disabled={!!busy}
          onChange={(event) => editName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void submitNew();
            }
          }}
          placeholder="e.g. Acme"
          aria-describedby={error ? 'workspace-create-error' : undefined}
          aria-invalid={
            !!error && lookup?.exactMatch && name.trim() === checkedName
          }
        />
        {error && (
          <p
            id="workspace-create-error"
            role="alert"
            className="break-words text-sm text-tone-danger"
          >
            {error}
          </p>
        )}
      </div>
      {!!lookup?.matches.length && (
        <div className="min-w-0 space-y-2">
          <p className="text-sm text-muted-foreground">
            These workspaces may be for the same project. Select one to request
            access, or create a separate workspace.
          </p>
          <ToggleGroup
            type="single"
            role="radiogroup"
            orientation="vertical"
            aria-label="Similar workspaces"
            value={selectedId}
            onValueChange={setSelectedId}
            disabled={!!busy}
            className="w-full flex-col items-stretch"
          >
            {matchGroup(
              lookup.matches.filter((match) => match.strength !== 'weak'),
              'Similar workspaces',
            )}
            {matchGroup(
              lookup.matches.filter((match) => match.strength === 'weak'),
              'Other possible matches',
            )}
          </ToggleGroup>
        </div>
      )}
      <DialogFooter className="flex-wrap gap-2">
        <Button
          variant="ghost"
          disabled={!!busy}
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
        <Button
          variant={lookup?.matches.length ? 'outline' : 'default'}
          onClick={() => void submitNew()}
          disabled={!name.trim() || !!busy}
        >
          {(busy === 'checking' || busy === 'creating') && (
            <Loader2
              aria-hidden
              className="size-4 animate-spin motion-reduce:animate-none"
            />
          )}
          {busy === 'checking'
            ? 'Checking…'
            : busy === 'creating'
              ? 'Creating…'
              : lookup
                ? 'Create new workspace'
                : 'Create'}
        </Button>
        {!!lookup?.matches.length && (
          <Button
            disabled={!selected || !!busy}
            onClick={() => void requestToJoin()}
          >
            {(busy === 'joining' || busy === 'opening') && (
              <Loader2
                aria-hidden
                className="size-4 animate-spin motion-reduce:animate-none"
              />
            )}
            {busy === 'opening'
              ? 'Opening…'
              : busy === 'joining'
                ? 'Requesting…'
                : selected?.isMember
                  ? 'Open'
                  : 'Request to join'}
          </Button>
        )}
      </DialogFooter>
    </AppDialog>
  );
}
